// ============================================================
// AppShell.js
// Authenticated layout wrapper. Renders the primary navigation +
// a main content area that holds the routed page.
//
// Wraps every authenticated route via <Outlet /> when used as
// a parent route, or as a direct wrapper:
//
//   <Route element={<AppShell />}>
//     <Route path="/tenants" element={<ProtectedRoute><TenantsPage /></ProtectedRoute>} />
//     ...
//   </Route>
//
// The <Toaster /> is mounted here so toasts persist across
// route changes.
//
// ── Two layouts, one nav ──────────────────────────────────
// Above --breakpoint-shell (900px) the navigation is the persistent
// 240px rail. Below it, that rail would take 62% of a 390px phone
// and leave the content column 150px wide — so it becomes an
// off-canvas drawer behind a slim top bar instead.
//
// Either the rail OR the drawer is rendered, never both: the nav
// exists in the DOM once, so there are no duplicate landmarks for a
// screen reader to walk and no second badge fetch. That is also why
// the choice is made in JS rather than by CSS visibility — and the
// drawer needs an imperative showModal() call regardless.
//
// The badge counts are fetched HERE and passed down to both the top
// bar and the drawer's nav, so opening the drawer costs no extra
// requests.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import SideNav from './SideNav';
import NavDrawer from './NavDrawer';
import MobileTopBar from './MobileTopBar';
import Toaster from './Toaster';
import useMediaQuery from '../hooks/useMediaQuery';
import useAdminBadgeCounts from '../hooks/useAdminBadgeCounts';
import './AppShell.css';

// Keep in lockstep with --breakpoint-shell in index.css. A media query
// condition cannot read a custom property, so the value is repeated
// here on purpose; tests/shellBreakpoint.test.js asserts the two agree.
export const SHELL_BREAKPOINT_QUERY = '(max-width: 900px)';

export default function AppShell() {
  const isCompact = useMediaQuery(SHELL_BREAKPOINT_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const badges = useAdminBadgeCounts();

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // A destination chosen inside the drawer must not leave the sheet
  // sitting over the page it just navigated to. SideNav's onNavigate
  // covers a link tap; this covers every other route change —
  // a redirect, a browser Back, a programmatic navigate.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Growing past the breakpoint with the drawer open would otherwise
  // leave a modal dialog trapping focus over the desktop rail.
  useEffect(() => { if (!isCompact) setDrawerOpen(false); }, [isCompact]);

  return (
    <div className={`app-shell ${isCompact ? 'app-shell--compact' : ''}`}>
      {isCompact ? (
        <>
          <MobileTopBar onOpenMenu={() => setDrawerOpen(true)} badges={badges} />
          <NavDrawer open={drawerOpen} onClose={closeDrawer} badges={badges} />
        </>
      ) : (
        <SideNav badges={badges} />
      )}

      <main className="app-shell__main">
        <Outlet />
      </main>
      <Toaster />
    </div>
  );
}
